import React from 'react'
import { useFetchAllBusinessProfiles } from '@@partner:graphql/query/businessProfiles/allBusinessProfiles.gql'
import { BUSINESS_PROFILE } from '@hive/status'
import { 
  BusinessOutline,
  ImageOutline,
  StarHalfOutline,
  CartOutline,
  CubeOutline,
  BicycleOutline,
  CheckmarkCircleOutline,
  TimeOutline
} from 'react-ionicons'

import { Col, Grid, Panel, Row, List, Tag, FlexboxGrid } from 'rsuite'

import './IndexPage.scss'
import { generatePath, Link } from 'react-router-dom'
import { 
  ROUTE_CATALOG_MODERATION_PRODUCT_REVIEWS, 
  ROUTE_MODERATE_BUSINESS_PROFILES, 
  ROUTE_UTIL_PRODUCT_PREVIEW_ADD,
  ROUTE_ORDERS_FEEDBACKS,
  ROUTE_ORDERS_VIEW_ORDER,
  ROUTE_CUSTOMERS_VIEW_CUSTOMER
} from '@@partner:config/routes'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { useFetchAllProductReviews } from '@@partner:graphql/query/product/productReviews/allProductReviews.gql'
import { PRODUCT_REVIEW_STATUS_AWAITING_MODERATION } from '@@partner:config/productReview-status-codes'
import { useQueryProducts } from '@@partner:graphql/query/catalog/products.gql'
import { useFetchOrdersByStatus } from '@@partner:graphql/query/order/ordersByStatus.gql'
import {
  ORDER_STATUS_PLACED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
  ORDER_STATUS_ASSEMBLY_COMPLETE,
  ORDER_STATUS_WAITING_FOR_COURIER,
  ORDER_STATUS_DEVIVERY_IN_PROGRESS,
  ORDER_STATUS_DELIVERED
} from '@@partner:config/order-status-codes'
import { ROUTE_ORDERS } from '@@partner:config/routes'
import StatCard from '@components/common/StatCard/StatCard'
import { useFetchAllCourierWorkShifts } from '@@partner:graphql/query/courier/allCourierWorkShifts.gql'
import { useQueryFeedbacks } from '@@partner:graphql/query/feedback/feedbacks.gql'
import UrgentOrdersPanel from '@components/common/UrgentOrdersPanel/UrgentOrdersPanel'
import type { Feedback } from '@@partner:types:app'

export default function IndexPage () {
  const { brandSlug, partnerInfo, isBrandLoaded } = usePartner()

  const { data } = useFetchAllBusinessProfiles({
    filter: {
      fields: [ 'status' ],
      op: 'eq',
      value: [ BUSINESS_PROFILE.PENDING ]
    },
    pager: {
      page: 1,
      limit: 1
    }
  })

  const { data: reviewData } = useFetchAllProductReviews({
    filter: {
      fields: [ 'status' ],
      op: 'eq',
      value: [ PRODUCT_REVIEW_STATUS_AWAITING_MODERATION ]
    },
    pager: {
      page: 1,
      limit: 1
    }
  })

  const { data: productsData } = useQueryProducts(
    {
      filter: {
        and: [
          {
            fields: [ 'preview_id' ],
            op: 'is_null'
          }
        ]
      },
      pager: {
        page: 1,
        limit: 1
      }
    }
  )

  const { data: ordersByStatus } = useFetchOrdersByStatus()

  // Подсчёт заказов по статусам
  const getCountByStatuses = (statuses: number[]) => 
    ordersByStatus?.ordersByStatus
      ?.filter(g => statuses.includes(g.status))
      ?.reduce((sum, g) => sum + g.count, 0) ?? 0

  const totalOrders = ordersByStatus?.ordersByStatus?.reduce((sum, g) => sum + g.count, 0) ?? 0
  const ordersPlaced = getCountByStatuses([ ORDER_STATUS_PLACED ])
  const ordersInAssembly = getCountByStatuses([ ORDER_STATUS_PAID, ORDER_STATUS_ASSEMBLY_IN_PROGRESS, ORDER_STATUS_ASSEMBLY_COMPLETE ])
  const ordersInDelivery = getCountByStatuses([ ORDER_STATUS_WAITING_FOR_COURIER, ORDER_STATUS_DEVIVERY_IN_PROGRESS ])
  const ordersDelivered = getCountByStatuses([ ORDER_STATUS_DELIVERED ])

  // Формирование URL с массивом статусов
  const buildOrdersUrl = (statuses: number[]) => {
    const params = new URLSearchParams()
    statuses.forEach((status, index) => {
      params.append(`statuses[${index}]`, String(status))
    })
    return `${generatePath(ROUTE_ORDERS, { brandSlug })}?${params.toString()}`
  }

  // Курьеры на смене
  const { data: courierShiftsData } = useFetchAllCourierWorkShifts({
    filter: {
      and: [
        { fields: [ 'is_started' ], op: 'eq', value: [ 'true' ] },
        { fields: [ 'is_finished' ], op: 'eq', value: [ 'false' ] }
      ]
    },
    pager: { page: 1, limit: 1 }
  })

  // Последние отзывы
  const { data: feedbacksData } = useQueryFeedbacks({
    pager: { page: 1, limit: 5 }
  })

  const activeCouriersCount = courierShiftsData?.allCourierWorkShifts?.pageInfo?.total ?? 0
  const latestFeedbacks: Feedback[] = feedbacksData?.feedbacks?.edges ?? []

  const productsWithoutPhoto = productsData?.products.pageInfo.total ?? 0
  const pendingReviewsCount = reviewData?.productReviews.pageInfo.total ?? 0
  const pendingBusinessProfilesCount = data?.allBusinessProfiles.pageInfo.total ?? 0

  if (!isBrandLoaded) {
    return (
      <div className="IndexPage">
        <h1 className="u-heading">
          Добро пожаловать в панель администратора! 😉
        </h1>
        <p>Загрузка данных бренда...</p>
      </div>
    )
  }

  return (
    <div className="IndexPage">
      <h1 className="u-heading">
        Добро пожаловать в панель администратора! 😉
      </h1>
      <Grid fluid>
        <Row>
          <Col xs={24} sm={12} md={8}>
            <StatCard
              title="Компаний на проверке"
              count={pendingBusinessProfilesCount}
              icon={<BusinessOutline color="#fff" height="32px" width="32px" />}
              color="#ec407a"
              link={generatePath(ROUTE_MODERATE_BUSINESS_PROFILES, { brandSlug })}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StatCard
              title="Отзывов на проверке"
              count={pendingReviewsCount}
              icon={<StarHalfOutline color="#fff" height="32px" width="32px" />}
              color="#ff9800"
              link={generatePath(ROUTE_CATALOG_MODERATION_PRODUCT_REVIEWS, { brandSlug })}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StatCard
              title="Товаров без фото"
              count={productsWithoutPhoto}
              icon={<ImageOutline color="#fff" height="32px" width="32px" />}
              color="#4caf50"
              link={generatePath(ROUTE_UTIL_PRODUCT_PREVIEW_ADD, { brandSlug })}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 8 }} className="IndexPage__OrdersRow">
          <Col xs={12} sm={8} md={8}>
            <StatCard
              title="Всего заказов"
              count={totalOrders}
              icon={<CartOutline color="#fff" height="32px" width="32px" />}
              color="#607d8b"
              link={generatePath(ROUTE_ORDERS, { brandSlug })}
            />
          </Col>
          <Col xs={12} sm={8} md={8}>
            <StatCard
              title="Размещены"
              count={ordersPlaced}
              icon={<TimeOutline color="#fff" height="32px" width="32px" />}
              color="#9c27b0"
              link={buildOrdersUrl([ ORDER_STATUS_PLACED ])}
            />
          </Col>
          <Col xs={12} sm={8} md={8}>
            <StatCard
              title="В сборке"
              count={ordersInAssembly}
              icon={<CubeOutline color="#fff" height="32px" width="32px" />}
              color="#ff9800"
              link={buildOrdersUrl([ ORDER_STATUS_PAID, ORDER_STATUS_ASSEMBLY_IN_PROGRESS, ORDER_STATUS_ASSEMBLY_COMPLETE ])}
            />
          </Col>
          <Col xs={12} sm={8} md={8}>
            <StatCard
              title="Доставляются"
              count={ordersInDelivery}
              icon={<BicycleOutline color="#fff" height="32px" width="32px" />}
              color="#2196f3"
              link={buildOrdersUrl([ ORDER_STATUS_WAITING_FOR_COURIER, ORDER_STATUS_DEVIVERY_IN_PROGRESS ])}
            />
          </Col>
          <Col xs={12} sm={8} md={8}>
            <StatCard
              title="Доставлены"
              count={ordersDelivered}
              icon={<CheckmarkCircleOutline color="#fff" height="32px" width="32px" />}
              color="#4caf50"
              link={buildOrdersUrl([ ORDER_STATUS_DELIVERED ])}
            />
          </Col>
        </Row>

        {/* Срочные заказы */}
        <Row style={{ marginTop: 24 }}>
          <Col xs={24}>
            <UrgentOrdersPanel brandSlug={brandSlug} />
          </Col>
        </Row>

        {/* Списки */}
        <Row style={{ marginTop: 24 }}>
          {/* Последние отзывы */}
          <Col xs={24} md={12}>
            <Panel 
              header={
                <FlexboxGrid justify="space-between" align="middle">
                  <span>Последние отзывы</span>
                  <Link to={generatePath(ROUTE_ORDERS_FEEDBACKS, { brandSlug })}>
                    <Tag color="blue">Все отзывы</Tag>
                  </Link>
                </FlexboxGrid>
              } 
              bordered 
              className="IndexPage__Panel"
            >
              {latestFeedbacks.length > 0 ? (
                <List>
                  {latestFeedbacks.map(feedback => {
                    const rating = feedback.general_rating ?? 0
                    return (
                      <List.Item key={feedback.id} className="IndexPage__FeedbackItem">
                        <FlexboxGrid justify="space-between" align="middle">
                          <FlexboxGrid.Item>
                            {feedback.customer?.id ? (
                              <Link 
                                to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, { brandSlug, customerId: feedback.customer.id })}
                                className="IndexPage__FeedbackCustomer IndexPage__FeedbackCustomer--link"
                              >
                                {feedback.customer?.first_name} {feedback.customer?.last_name}
                              </Link>
                            ) : (
                              <span className="IndexPage__FeedbackCustomer">
                                {feedback.customer?.first_name} {feedback.customer?.last_name}
                              </span>
                            )}
                            {feedback.order?.id && (
                              <Link 
                                to={generatePath(ROUTE_ORDERS_VIEW_ORDER, { brandSlug, orderId: feedback.order.id })}
                                className="IndexPage__FeedbackOrder"
                              >
                                Заказ #{feedback.order.id}
                              </Link>
                            )}
                            <span className="IndexPage__FeedbackShop">
                              {feedback.shop?.name}
                            </span>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item>
                            <Tag color={rating >= 4 ? 'green' : rating >= 3 ? 'orange' : 'red'}>
                              ★ {rating}
                            </Tag>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                        {feedback.review && (
                          <div className="IndexPage__FeedbackReview">{feedback.review}</div>
                        )}
                      </List.Item>
                    )
                  })}
                </List>
              ) : (
                <div className="IndexPage__EmptyState">Нет отзывов</div>
              )}
            </Panel>
          </Col>
        </Row>
      </Grid>
    </div>
  )
}
